import React, {Children} from 'react';
import {useTranslation} from 'react-i18next';
import {
  AccessibilityInfo,
  findNodeHandle,
  TouchableOpacity,
  View,
} from 'react-native';
import {noop} from 'lodash';
import {flow, map} from 'lodash/fp';
import {v4 as uuidv4} from 'uuid';

import Icon from '/component/base/Icon';
import BasePopover from '/component/base/Popover';
import ProfileAttribute from '/component/base/ProfileAttribute';
import Text from '/component/base/Text';
import ProfileSection from '/component/partial/profile/ProfileSection';
import {DownlineProfile} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import testProperties from '/util/testing/testProperties';

import {styles} from './UrlDetailsPopover.style';
import {getListItems, IconNameByType} from './UrlDetailsPopover.util';

// Define types for the props
interface UrlDetailsPopoverProps {
  tt: (keyPrefix: any, value: any) => string;
  popoverClose: () => void;
  setSocialLinkText: (map: Map<string, any>) => void;
  setSocialLinks: (links: any[]) => void;
  socialLinkText: Map<string, any>;
  socialLinks: string[];
  // addLinkButtonRef: React.RefObject<any>;
  returnFocusRef: React.RefObject<any>;
}

export const UrlDetailsPopover: React.FC<UrlDetailsPopoverProps> = ({
  tt = withTranslation.ttDefaultProp,
  popoverClose = noop,
  setSocialLinkText = noop,
  setSocialLinks = noop,
  socialLinkText,
  socialLinks = [],
  returnFocusRef, // Receive ref prop for "Add a link" button
}) => {
  const handleCloseModal = () => {
    popoverClose();

    if (returnFocusRef?.current) {
      try {
        const reactTag = findNodeHandle(returnFocusRef.current);
        if (reactTag) {
          AccessibilityInfo.setAccessibilityFocus(reactTag);
        }
      } catch (_e) {}
    }
  };
  const urlDetails = getListItems();
  const {t} = useTranslation();

  const handlePressSocials = (urlDetail: any, linkID: string) => {
    setSocialLinkText(socialLinkText.set(linkID, {}));
    setSocialLinks([...socialLinks, urlDetail]);
    popoverClose();
  };

  const renderSocials = (urlDetail: any) => {
    const linkID = `${urlDetail.label}-${uuidv4()}`;
    urlDetail.id = linkID;

    const label = t(`${urlDetail.label}`, urlDetail.label);

    return (
      <ProfileAttribute
        // @ts-expect-error TS(2322): Type 'any[]' is not assignable to type 'string'.
        iconName={IconNameByType[urlDetail.iconName]}
        label={label}
        style={{base: styles.socialLinkProfileAttribute}}
        accessibilityRole="button"
        accessibilityLabel={`${label}`}
        accessibilityHint={`Double tap to add ${label} link`}
        onPress={() => handlePressSocials(urlDetail, linkID)}
        testID={urlDetail.label}
      />
    );
  };

  return (
    <BasePopover style={styles.modalView} padded>
      <View style={styles.modalHeader} accessible={false}>
        <View
          accessible={false}
          {...testProperties(DownlineProfile.candidateLinksModal, false)}>
          <TouchableOpacity
            onPress={handleCloseModal}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel="Close">
            <Icon name="close" />
          </TouchableOpacity>
        </View>
        <View
          accessible={false}
          {...testProperties(DownlineProfile.candidateLinksModalHeader, false)}>
          <Text
            accessible={true}
            accessibilityRole="header"
            accessibilityLabel={tt('links', 'Link to')}
            style={styles.modalHeaderText}>
            {tt('links', 'Link to')}
          </Text>
        </View>
      </View>
      <View>
        <ProfileSection style={{base: styles.socialLinkBase}}>
          {flow([map(renderSocials), Children.toArray])(urlDetails)}
        </ProfileSection>
      </View>
      <TouchableOpacity
        onPress={popoverClose}
        accessibilityRole="button"
        accessibilityLabel="Cancel"
        accessibilityHint="Dismiss this dialog"
        accessible={true}
        {...testProperties(null, false)}>
        <View style={styles.cancelButton}>
          <Text
            variant="button"
            style={styles.buttonText}
            {...testProperties(
              DownlineProfile.candidateLinksModalCancelButton,
            )}>
            {tt('cancel', 'Cancel')}
          </Text>
        </View>
      </TouchableOpacity>
    </BasePopover>
  );
};

export default withTranslation('UrlDetailsPopoverModal')(UrlDetailsPopover);
